import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import {
  copyGroupConfigVersion,
  createGroupConfigVersion,
  deleteGroupConfigVersion,
  listGroupConfigs,
  listGroupConfigElementBindings,
  listGroupConfigSegments,
  listGroupConfigVersions,
  setGroupConfigVersionStatus,
} from '../../../api/groupConfigs.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'

function formatDate(value) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

export default function GroupConfigDetailPage() {
  const outlet = useOutletContext()
  const { groupConfigId, id: applicationIdFromParams } = useParams()
  const applicationId = outlet?.applicationId || applicationIdFromParams
  const navigate = useNavigate()
  const [isCreatingConfig, setIsCreatingConfig] = useState(false)
  const { confirm, toast } = useFeedback()
  const [statusHintByVersionId, setStatusHintByVersionId] = useState({})
  const [state, setState] = useState({
    status: 'loading',
    groupConfigs: [],
    versions: [],
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    setState({
      status: 'loading',
      groupConfigs: [],
      versions: [],
      error: null,
    })

    Promise.all([listGroupConfigs(applicationId), listGroupConfigVersions(applicationId, groupConfigId)])
      .then(([groupConfigs, versions]) => {
        if (cancelled) return
        setState({
          status: 'ok',
          groupConfigs,
          versions,
          error: null,
        })
      })
      .catch((error) => {
        if (cancelled) return
        setState({
          status: 'error',
          groupConfigs: [],
          versions: [],
          error,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, groupConfigId])

  const groupConfig = useMemo(() => {
    return state.groupConfigs.find((item) => String(item.id) === String(groupConfigId)) || null
  }, [groupConfigId, state.groupConfigs])

  async function validateVersionReadyForStatusSwitch(versionId) {
    const [segments, bindings] = await Promise.all([
      listGroupConfigSegments(applicationId, groupConfigId, { remoteConfigId: versionId }),
      listGroupConfigElementBindings(applicationId, groupConfigId, { remoteConfigId: versionId }),
    ])
    if (!segments.length) return true

    const bindingsBySegmentId = {}
    for (const binding of bindings) {
      const segmentId = String(binding.groupSegmentId || '')
      if (!segmentId) continue
      bindingsBySegmentId[segmentId] = true
    }

    return segments.every((segment) => Boolean(bindingsBySegmentId[String(segment.id)]))
  }

  function isVersionReadOnly(version) {
    const s = String(version?.status || '').trim().toLowerCase()
    return s === 'released'
  }

  async function handleSetVersionStatus(version, nextStatus) {
    if (!nextStatus || nextStatus === version.status) return
    if (isVersionReadOnly(version)) return
    setStatusHintByVersionId((prev) => ({
      ...prev,
      [version.id]: false,
    }))
    try {
      const isVersionReadyForStatusSwitch = await validateVersionReadyForStatusSwitch(version.id)
      if (!isVersionReadyForStatusSwitch) {
        setStatusHintByVersionId((prev) => ({
          ...prev,
          [version.id]: true,
        }))
        return
      }

      const updated = await setGroupConfigVersionStatus(applicationId, groupConfigId, version.id, nextStatus)
      setState((prev) => ({
        ...prev,
        versions: prev.versions.map((item) =>
          String(item.id) === String(version.id)
            ? {
                ...item,
                status: updated?.status || nextStatus,
                availableStatuses:
                  Array.isArray(updated?.availableStatuses) && updated.availableStatuses.length
                    ? updated.availableStatuses
                    : item.availableStatuses,
              }
            : item,
        ),
      }))
    } catch (error) {
      toast({
        type: 'error',
        message: String(error?.message || error),
      })
    }
  }

  function getOpenConfigPath(versionId) {
    return `/applications/${applicationId}/configs/${groupConfigId}/builder?remoteConfigId=${encodeURIComponent(
      versionId,
    )}`
  }

  async function handleCopyConfig(version) {
    try {
      const copied = await copyGroupConfigVersion(applicationId, groupConfigId, version.id)
      if (!copied) return
      setState((prev) => ({
        ...prev,
        versions: [copied, ...prev.versions],
      }))
    } catch (error) {
      toast({
        type: 'error',
        message: String(error?.message || error),
      })
    }
  }

  async function handleDeleteConfig(version) {
    if (isVersionReadOnly(version)) return
    const confirmed = await confirm({
      title: 'Delete config version',
      message: 'Delete this config version? This action cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteGroupConfigVersion(applicationId, groupConfigId, version.id)
      setState((prev) => ({
        ...prev,
        versions: prev.versions.filter((item) => String(item.id) !== String(version.id)),
      }))
    } catch (error) {
      toast({
        type: 'error',
        message: String(error?.message || error),
      })
    }
  }

  async function handleCreateConfig() {
    if (isCreatingConfig) return
    setIsCreatingConfig(true)
    try {
      const created = await createGroupConfigVersion(applicationId, groupConfigId)
      if (!created?.id) return
      navigate(
        `/applications/${applicationId}/configs/${groupConfigId}/builder?remoteConfigId=${encodeURIComponent(
          created.id,
        )}`,
      )
    } catch (error) {
      toast({
        type: 'error',
        message: String(error?.message || error),
      })
    } finally {
      setIsCreatingConfig(false)
    }
  }

  if (state.status === 'loading') {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Loading configs...</div>
      </section>
    )
  }

  if (state.status === 'error') {
    return (
      <section className="card sectionCard">
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error)}</pre>
      </section>
    )
  }

  return (
    <section className="card sectionCard">
      <div className="pageHeader pageHeaderTight">
        <div>
          <div className="cardTitle">{groupConfig?.name || 'Group config versions'}</div>
          <div className="muted">
            Start bundle version: <strong>{groupConfig?.startBundleVersion || '—'}</strong>
          </div>
        </div>
        <div className="buttonGroup">
          <Link to={`/applications/${applicationId}/configs`} className="button buttonGhost">
            Back
          </Link>
          <button
            type="button"
            className="button buttonPrimary"
            onClick={handleCreateConfig}
            disabled={isCreatingConfig}
          >
            {isCreatingConfig ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>

      {state.versions.length === 0 ? (
        <p className="muted inlineMessage">
          No versions yet.
        </p>
      ) : (
        <table className="table" aria-label="Group config versions list">
          <thead>
            <tr>
              <th>Bundle version</th>
              <th>Status</th>
              <th>Created</th>
              <th>Updated</th>
              <th className="alignRight"></th>
            </tr>
          </thead>
          <tbody>
            {state.versions.map((version) => {
              const versionReadOnly = isVersionReadOnly(version)
              return (
              <tr key={version.id}>
                <td>{version.bundleVersion || ''}</td>
                <td>
                  <select
                    className="textInput textInputCompact"
                    value={version.status || 'Draft'}
                    onChange={(e) => handleSetVersionStatus(version, e.target.value)}
                    disabled={versionReadOnly}
                  >
                    {(
                      version.availableStatuses?.length
                        ? version.availableStatuses
                        : [version.status || 'Draft']
                    ).map((statusValue) => (
                      <option key={statusValue} value={statusValue}>
                        {statusValue}
                      </option>
                    ))}
                  </select>
                  {statusHintByVersionId[version.id] ? (
                    <p className="muted inlineMessage">
                      Select an element for each segment before changing status.
                    </p>
                  ) : null}
                </td>
                <td>{formatDate(version.createdAt)}</td>
                <td>{formatDate(version.updatedAt)}</td>
                <td className="alignRight">
                  <span className="tableActions">
                    <Link to={getOpenConfigPath(version.id)} className="button buttonGhost">
                      View
                    </Link>
                    <button
                      type="button"
                      className="button buttonGhost"
                      onClick={() => handleCopyConfig(version)}
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      className="button buttonGhost buttonDanger"
                      onClick={() => handleDeleteConfig(version)}
                      disabled
                    >
                      Delete
                    </button>
                  </span>
                </td>
              </tr>
            )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}

